import { type useDeleteProductizedServiceMutation } from '#app/__generated__/relay/useDeleteProductizedServiceMutation.graphql.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { logger } from '#app/services/logger.js';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useMemo } from 'react';

const log = logger.child({
  namespace: 'hooks:useDeleteProductizedService',
});

type UseDeleteProductizedServiceProps = {
  id: null | string;
  isMovingToDraft?: boolean;
  onDeleteCompletion?: () => void;
  onMoveToDraft?: () => void;
};

export const useDeleteProductizedService = ({
  id,
  isMovingToDraft = false,
  onDeleteCompletion,
  onMoveToDraft,
}: UseDeleteProductizedServiceProps): [
  onOpenDeleteProductizedServiceModal: () => void,
  onDelete: (closeModal: VoidFunction, isSuggestedService?: boolean) => void,
] => {
  const toast = useToast();

  const [deleteResource, isDeleting] =
    useContraMutation<useDeleteProductizedServiceMutation>(graphql`
      mutation useDeleteProductizedServiceMutation(
        $input: DeleteProductizedServiceInput!
      ) {
        deleteProductizedService(input: $input) {
          errors {
            __typename
            message
          }

          userAccount {
            ...OpenProfileCompletionChecklistButton_userAccount
            id

            profile {
              id
              productizedServices {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    `);

  const onDelete = useCallback(
    (closeModal: VoidFunction, isSuggestedService?: boolean) => {
      if (id) {
        deleteResource({
          onCompleted: (errors, response) => {
            const { apiErrors, payloadErrors } = errors;

            if (payloadErrors) {
              for (const payloadError of payloadErrors) {
                captureException(payloadError.message, {
                  extra: {
                    locations: payloadError.locations,
                    severity: payloadError.severity,
                  },
                });
                toast.error(
                  'An unexpected error occurred. Our team has been notified.',
                );
              }

              return;
            }

            if (apiErrors) {
              for (const apiError of apiErrors) {
                toast.error(apiError.message);
                log.debug(apiError, apiError.message);
              }

              return;
            }

            if (response.deleteProductizedService.userAccount) {
              if (isSuggestedService) {
                toast.success('Removed suggested service.');
              } else {
                toast.success('You successfully deleted your service');
              }

              closeModal();
              onDeleteCompletion?.();
            } else {
              // Failure
              log.debug('Something went wrong when attempting to delete.');
            }
          },
          onError: (error) => {
            captureException(error);
            toast.error(
              'Oops! Something went wrong. Our team has been notified. Please try again.',
            );
          },
          variables: {
            input: {
              id,
            },
          },
        });
      } else {
        captureException(
          new Error('Attempting to delete a service without an ID!'),
        );

        log.error('Attempting to delete a service without an ID!');
      }
    },
    [deleteResource, id, onDeleteCompletion, toast],
  );

  const { openModal: openDeleteModal } = useModal({
    identifier: 'DeleteResourceModal',
    modalProps: {
      isDeleting,
      isMovingToDraft,
      onDelete,
      onMoveToDraft: onMoveToDraft
        ? (closeModal) => {
            onMoveToDraft();
            closeModal();
          }
        : undefined,
      resourceType: 'service',
    },
  });

  return useMemo(
    () => [openDeleteModal, onDelete],
    [onDelete, openDeleteModal],
  );
};
